import { create } from 'zustand';
import { useAuthStore } from './useAuthStore';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getLocalDateString } from '../utils/formatDate';

interface WaterState {
  dailyGoal: number;
  currentAmount: number;
  streak: number;
  lastCheckDate: string;
  monthlyStats: {
    [key: string]: number;
  };
  reminderInterval: number;
  isTimerActive: boolean;
  timeRemaining: number;
  addWater: (amount: number, multiplier?: number, drinkDetails?: { name: string; color: string }) => void;
  resetDaily: () => void;
  setReminderInterval: (minutes: number) => void;
  setTimerActive: (active: boolean) => void;
  setTimeRemaining: (seconds: number) => void;
  resetTimer: () => void;
  updateStreak: (newStreak: number) => void;
  checkAndUpdateStreak: () => void;
  setDailyGoal: (goal: number) => void;
  checkInDates: string[];
  triggerConfettiAnimation: boolean;
  drinkHistory: Array<{
    id: string;
    amount: number;
    multiplier: number;
    name: string;
    color: string;
    timestamp: number;
  }>;
}

export const useWaterStore = create<WaterState>((set) => ({
  dailyGoal: 2000, // ml
  currentAmount: 0,
  streak: 0,
  lastCheckDate: getLocalDateString(new Date()),
  monthlyStats: {},
  reminderInterval: 30, // default 30 minutes
  isTimerActive: false,
  timeRemaining: 30 * 60, // in seconds
  checkInDates: [],
  triggerConfettiAnimation: false,
  drinkHistory: [],

  addWater: async (amount, multiplier = 1, drinkDetails = { name: 'Water', color: 'bg-blue-500' }) => {
    const user = useAuthStore.getState().user;
    const userData = useAuthStore.getState().userData;
    const isGuest = useAuthStore.getState().isGuest;
    const currentState = useWaterStore.getState();

    // Sanitize input: ensure amount is a positive number and not unreasonably large
    const sanitizedAmount = Math.max(0, Math.min(10000, Math.floor(amount))); // Max 10L per addition
    if (sanitizedAmount === 0) return; // Don't add zero or negative amounts

    // Apply multiplier
    const actualAmount = Math.floor(sanitizedAmount * multiplier);

    const newAmount = currentState.currentAmount + actualAmount;
    const today = getLocalDateString(new Date());

    // Create new drink entry
    const newDrink = {
      id: Date.now().toString(),
      amount: actualAmount,
      multiplier,
      name: drinkDetails.name,
      color: drinkDetails.color,
      timestamp: Date.now(),
    };

    // First update local state
    set((state) => ({
      currentAmount: newAmount,
      timeRemaining: state.reminderInterval * 60,
      monthlyStats: {
        ...state.monthlyStats,
        [today]: newAmount,
      },
      drinkHistory: [...state.drinkHistory, newDrink],
    }));

    // Check if daily goal is met and update streak
    if (newAmount >= currentState.dailyGoal && currentState.currentAmount < currentState.dailyGoal && !currentState.checkInDates.includes(today)) {
      // Goal just met, increase streak immediately
      const newStreak = currentState.streak + 1;
      set({ streak: newStreak });

      // Add current date to checkInDates
      const today = getLocalDateString(new Date());
      set((state) => ({
        checkInDates: [...state.checkInDates, today],
      }));

      // Update Firebase if user is authenticated and not a guest
      if (user && userData && !isGuest) {
        try {
          const userRef = doc(db, 'users', user.uid);
          await updateDoc(userRef, {
            waterIntake: newAmount,
            streak: newStreak,
            checkInDates: [...userData.checkInDates, today],
            monthlyStats: {
              ...userData.monthlyStats,
              [today]: newAmount,
            },
            drinkHistory: [...currentState.drinkHistory, newDrink],
          });

          // Update the userData in auth store
          useAuthStore.setState({
            userData: {
              ...userData,
              waterIntake: newAmount,
              streak: newStreak,
              checkInDates: [...userData.checkInDates, today],
              monthlyStats: {
                ...userData.monthlyStats,
                [today]: newAmount,
              },
              drinkHistory: [...currentState.drinkHistory, newDrink],
            }
          });
        } catch (error) {
          console.error('Failed to update water intake and streak in Firebase:', error);
        }
      }
    } else {
      // Only update water intake if streak didn't change
      if (user && userData && !isGuest) {
        try {
          const userRef = doc(db, 'users', user.uid);
          await updateDoc(userRef, {
            waterIntake: newAmount,
            monthlyStats: {
              ...userData.monthlyStats,
              [today]: newAmount,
            },
            drinkHistory: [...currentState.drinkHistory, newDrink],
          });

          // Update the userData in auth store
          useAuthStore.setState({
            userData: {
              ...userData,
              waterIntake: newAmount,
              monthlyStats: {
                ...userData.monthlyStats,
                [today]: newAmount,
              },
              drinkHistory: [...currentState.drinkHistory, newDrink],
            }
          });
        } catch (error) {
          console.error('Failed to update water intake in Firebase:', error);
        }
      }
    }
  },

  checkAndUpdateStreak: async () => {
    const today = getLocalDateString(new Date());
    const state = useWaterStore.getState();
    const user = useAuthStore.getState().user;
    const userData = useAuthStore.getState().userData;
    const isGuest = useAuthStore.getState().isGuest;

    if (state.lastCheckDate !== today) {
      // It's a new day
      const yesterdayMet = state.currentAmount >= state.dailyGoal;

      if (!yesterdayMet && state.currentAmount > 0) {
        // Reset streak if yesterday's goal wasn't met and some water was consumed
        await useWaterStore.getState().updateStreak(0);
      }

      // Reset water intake for the new day
      set({
        currentAmount: 0,
        lastCheckDate: today,
        drinkHistory: [],
      });

      // Update Firebase if user is authenticated and not a guest
      if (user && userData && !isGuest) {
        try {
          const userRef = doc(db, 'users', user.uid);
          await updateDoc(userRef, {
            waterIntake: 0,
            lastCheckDate: today,
            drinkHistory: [],
          });

          // Update the userData in auth store
          useAuthStore.setState({
            userData: {
              ...userData,
              waterIntake: 0,
              lastCheckDate: today,
              drinkHistory: [],
            }
          });
        } catch (error) {
          console.error('Failed to update daily reset in Firebase:', error);
        }
      }
    }
  },

  resetDaily: async () => {
    const user = useAuthStore.getState().user;
    const userData = useAuthStore.getState().userData;
    const isGuest = useAuthStore.getState().isGuest;
    const today = getLocalDateString(new Date());

    set({
      currentAmount: 0,
      lastCheckDate: today,
      drinkHistory: [],
    });

    // Reset Firebase if user is authenticated and not a guest
    if (user && userData && !isGuest) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          waterIntake: 0,
          lastCheckDate: today,
          drinkHistory: [],
        });

        // Update the userData in auth store
        useAuthStore.setState({
          userData: {
            ...userData,
            waterIntake: 0,
            lastCheckDate: today,
            drinkHistory: [],
          }
        });
      } catch (error) {
        console.error('Failed to reset water intake in Firebase:', error);
      }
    }
  },

  setReminderInterval: async (minutes) => {
    const user = useAuthStore.getState().user;
    const userData = useAuthStore.getState().userData;
    const isGuest = useAuthStore.getState().isGuest;

    // Sanitize input: ensure minutes is a positive number within reasonable bounds
    const sanitizedMinutes = Math.max(1, Math.min(120, Math.floor(minutes))); // Between 1 and 120 minutes

    set({
      reminderInterval: sanitizedMinutes,
      timeRemaining: sanitizedMinutes * 60
    });

    // Update Firebase if user is authenticated and not a guest
    if (user && userData && !isGuest) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          reminderInterval: sanitizedMinutes
        });

        // Update the userData in auth store
        useAuthStore.setState({
          userData: {
            ...userData,
            reminderInterval: sanitizedMinutes
          }
        });
      } catch (error) {
        console.error('Failed to update reminder interval in Firebase:', error);
      }
    }
  },

  updateStreak: async (newStreak) => {
    const user = useAuthStore.getState().user;
    const userData = useAuthStore.getState().userData;
    const isGuest = useAuthStore.getState().isGuest;

    set({ streak: newStreak });

    // Update Firebase if user is authenticated and not a guest
    if (user && userData && !isGuest) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          streak: newStreak
        });

        // Update the userData in auth store
        useAuthStore.setState({
          userData: {
            ...userData,
            streak: newStreak
          }
        });
      } catch (error) {
        console.error('Failed to update streak in Firebase:', error);
      }
    }
  },

  setTimerActive: (active) => set((state) => ({
    isTimerActive: active,
    timeRemaining: active ? state.reminderInterval * 60 : state.timeRemaining
  })),

  setTimeRemaining: (seconds) => set({ timeRemaining: seconds }),

  resetTimer: () => set((state) => ({ timeRemaining: state.reminderInterval * 60 })),

  setDailyGoal: async (goal) => {
    const user = useAuthStore.getState().user;
    const userData = useAuthStore.getState().userData;
    const isGuest = useAuthStore.getState().isGuest;

    // Sanitize input: ensure goal is a positive number and within reasonable bounds
    const sanitizedGoal = Math.max(500, Math.min(10000, Math.floor(goal))); // Between 500ml and 10L

    set({ dailyGoal: sanitizedGoal });

    // Update Firebase if user is authenticated and not a guest
    if (user && userData && !isGuest) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          dailyGoal: sanitizedGoal
        });

        // Update the userData in auth store
        useAuthStore.setState({
          userData: {
            ...userData,
            dailyGoal: sanitizedGoal
          }
        });
      } catch (error) {
        console.error('Failed to update daily goal in Firebase:', error);
      }
    }
  }
}));